import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { User, Mail, Calendar, Users, Trophy, Clock, Zap, Award, Edit3, Key, Phone, Edit, Building2, Settings, Star, ExternalLink, AlertCircle, ArrowLeft, Heart, HelpCircle, MessageCircle, Shield, Bell, LogOut, ChevronRight, Camera, MapPin, MessageSquare } from "lucide-react";
import Navigation from "./Navigation";
import CitySelector from "./CitySelector";
import { useAuth } from "../contexts/AuthContext";
import { useForm, useApi, useFetch } from "../hooks/useApi";
import { apiService } from "../services/apiService";
import { validateEmail, validatePhone, validatePassword, validateRequired } from "../utils/validation";
import { toast } from "sonner";
import ClubCard from "./ui/ClubCard";
import RunCard from "./ui/RunCard";
import { convertImagePositionToFloat, DEFAULT_IMAGE_POSITION } from "../utils/dataTransformers";
import { compressImageForUpload } from "../utils/imageCompression";
import { getPrimaryImageUrl } from "../utils/imageUtils";
import { formatPhoneNumberDisplay, sanitizePhoneInput } from "../utils/phoneUtils";
import ClubViewButton from "./ui/ClubViewButton";
import ScrollableClubCardModal from "./ui/ScrollableClubCardModal";
import StravaConnection from "./StravaConnection";
import { UI } from "../constants/designTokens";
import { parseMemberResponse } from "../utils/memberUtils";
import { CLUB_TYPES, SKILL_LEVELS } from "../constants/clubConstants";
import { formatProfileLocationLabel } from "../utils/locationUtils";

// Feedback Form Component
const FeedbackForm = ({ user, onClose }) => {
  const [feedbackType, setFeedbackType] = useState('Feedback');
  const [message, setMessage] = useState('');
  const { execute: executeSubmit, loading: submitting } = useApi();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      toast.error('Please enter your feedback or bug report.');
      return;
    }

    if (message.trim().length < 10) {
      toast.error('Please provide more details (at least 10 characters).');
      return;
    }

    try {
      // Get browser info
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      const deviceInfo = `${platform} - ${userAgent}`;

      await executeSubmit(
        () => apiService.post('/users/me/feedback', {
          feedback_type: feedbackType,
          message: message.trim(),
          app_version: 'web',
          device_info: deviceInfo,
        }),
        {
          showSuccessToast: true,
          successMessage: 'Thank you! Your feedback has been submitted successfully.'
        }
      );

      setMessage('');
      setFeedbackType('Feedback');
      onClose();
    } catch (error) {
      // Error handled by useApi hook
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <Label className="text-runcards-text-primary text-runcards-md font-runcards-bold mb-3 block">
          Type
        </Label>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setFeedbackType('Feedback')}
            disabled={submitting}
            className={`flex-1 px-4 py-3 rounded-runcards-lg border font-runcards font-runcards-bold text-runcards-md transition-all duration-200 ${
              feedbackType === 'Feedback'
                ? 'bg-runcards-accent border-runcards-accent text-runcards-text-primary'
                : 'bg-runcards-foreground/50 border-runcards-border/20 text-runcards-text-secondary hover:bg-runcards-foreground/70 hover:border-runcards-border/40'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Feedback
          </button>
          <button
            type="button"
            onClick={() => setFeedbackType('Bug Report')}
            disabled={submitting}
            className={`flex-1 px-4 py-3 rounded-runcards-lg border font-runcards font-runcards-bold text-runcards-md transition-all duration-200 ${
              feedbackType === 'Bug Report'
                ? 'bg-runcards-accent border-runcards-accent text-runcards-text-primary'
                : 'bg-runcards-foreground/50 border-runcards-border/20 text-runcards-text-secondary hover:bg-runcards-foreground/70 hover:border-runcards-border/40'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Bug Report
          </button>
        </div>
      </div>

      <div>
        <Label htmlFor="feedback-message" className="text-runcards-text-primary text-runcards-md font-runcards-bold mb-3 block">
          Message
        </Label>
        <Textarea
          id="feedback-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={
            feedbackType === 'Bug Report'
              ? 'Describe the bug you encountered...'
              : 'Share your feedback or suggestions...'
          }
          className="min-h-[150px] bg-runcards-background/50 border-runcards-border/20 text-runcards-text-primary font-runcards text-runcards-md rounded-runcards-lg placeholder:text-runcards-text-secondary/50 focus:border-runcards-accent/50 transition-colors"
          disabled={submitting}
          maxLength={5000}
        />
        <p className="text-runcards-text-secondary text-runcards-sm text-right mt-2 font-runcards">
          {message.length}/5000 characters
        </p>
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={submitting}
          className="flex-1 border-runcards-border/20 text-runcards-text-primary hover:bg-runcards-foreground/50 hover:border-runcards-border/40 font-runcards font-runcards-bold rounded-runcards-lg"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={submitting || !message.trim()}
          className="flex-1 bg-runcards-accent text-runcards-text-primary hover:bg-runcards-accent/80 font-runcards font-runcards-bold rounded-runcards-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
      </div>
    </form>
  );
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, setUser, loading, updateClubInfo, updateEmail, updateProfilePicture, logout } = useAuth();
  const { execute: executeUpdate, loading: updateLoading } = useApi();
  const [profileLoading, setProfileLoading] = useState(true);
  // Remove old state variables - now handled by useFetch hook
  const [isEditingClub, setIsEditingClub] = useState(false);
  const [clubEditData, setClubEditData] = useState({});
  const [clubEditLoading, setClubEditLoading] = useState(false);
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(user?.city || '');
  const [locationLoading, setLocationLoading] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  // Use our form hooks for better state management
  const passwordForm = useForm(
    {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    {
      currentPassword: (value) => validateRequired(value, 'Current password'),
      newPassword: (value) => validatePassword(value, {
        minLength: 8,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true
      }),
      confirmPassword: (value) => {
        if (!value) return 'Please confirm your password';
        if (value !== passwordForm.values.newPassword) {
          return 'Passwords do not match';
        }
        return null;
      }
    }
  );

  const phoneForm = useForm(
    { phone: user?.phone || '' },
    { phone: validatePhone }
  );

  const emailForm = useForm(
    { email: user?.email || '' },
    { email: validateEmail }
  );

  // Use our API hooks for form submissions
  const { execute: executePasswordUpdate, loading: passwordLoading } = useApi();
  const { execute: executePhoneUpdate, loading: phoneLoading } = useApi();

  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [phoneData, setPhoneData] = useState({ phone: user?.phone || '' });
  const [phoneErrors, setPhoneErrors] = useState({});
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [emailData, setEmailData] = useState({ email: user?.email || '' });
  const [emailErrors, setEmailErrors] = useState({});
  const [profilePictureData, setProfilePictureData] = useState({ profile_picture: user?.profile_picture || '' });
  const [profilePictureErrors, setProfilePictureErrors] = useState({});
  const [profilePictureLoading, setProfilePictureLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [clubUpdateLoading, setClubUpdateLoading] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingProfilePicture, setIsEditingProfilePicture] = useState(false);
  const [emailUpdateLoading, setEmailUpdateLoading] = useState(false);
  const [isEditingClubImage, setIsEditingClubImage] = useState(false);
  const [clubImageData, setClubImageData] = useState({ image: '', description: '' });
  const [clubImageErrors, setClubImageErrors] = useState({});
  const [clubImageLoading, setClubImageLoading] = useState(false);
  const [selectedClubImageFile, setSelectedClubImageFile] = useState(null);
  const [clubImagePreviewUrl, setClubImagePreviewUrl] = useState(null);
  
  // Club image positioning state
  const [clubImagePosition, setClubImagePosition] = useState(DEFAULT_IMAGE_POSITION);
  const [isClubImagePositioning, setIsClubImagePositioning] = useState(false);
  const [clubImageDragStart, setClubImageDragStart] = useState({ x: 0, y: 0 });
  const [isClubImageDragging, setIsClubImageDragging] = useState(false);
  const [isEditingRunCardImage, setIsEditingRunCardImage] = useState(false);
  const [runCardImageData, setRunCardImageData] = useState({ image: '', description: '' });
  const [runCardImageErrors, setRunCardImageErrors] = useState({});
  const [runCardImageLoading, setRunCardImageLoading] = useState(false);
  const [selectedRunCardImageFile, setSelectedRunCardImageFile] = useState(null);
  const [runCardImagePreviewUrl, setRunCardImagePreviewUrl] = useState(null);
  
  const [showRunCard, setShowRunCard] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [loadingRunCard, setLoadingRunCard] = useState(false);
  const [showClubCardModal, setShowClubCardModal] = useState(false);
  const [isRunnerEditModalOpen, setIsRunnerEditModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  // Use our fetch hook for club data - use existing getById endpoint
  const { data: clubData, loading: clubLoading, refetch: refetchClub, error: clubError } = useFetch(
    () => apiService.clubs.getById(user?.club_id),
    [user?.club_id],
    { enabled: !!user?.club_id }
  );


  // Fetch club members for run club users
  const { data: clubMembersResponse, loading: clubMembersLoading } = useFetch(
    () => apiService.clubs.getClubMembers(user?.club_id, 200, 0),
    [user?.club_id],
    { enabled: !!user?.club_id && user?.user_type === 'runclub' }
  );
  
  // Extract members array from response using shared utility
  const clubMembers = parseMemberResponse(clubMembersResponse);


  // Initialize club image position from existing data
  useEffect(() => {
    if (clubData?.imagePosition) {
      setClubImagePosition(clubData.imagePosition);
    }
  }, [clubData]);



  // Create preview club data for ClubCard component
  const previewClub = {
    id: clubData?.id,
    name: clubData?.name,
    type: clubData?.type || 'Social',
    runcard_image: getPrimaryImageUrl(clubData?.clubcard_images),
    imagePosition: clubImagePosition,
    organizer_name: user.name,
    organizer: user.name,
    members: clubData?.members || 0,
    city: clubData?.city || 'Unknown',
    nextRun: clubData?.nextRun || null,
    description: clubData?.description,
    rating: clubData?.rating || 0.0,
    total_ratings: clubData?.total_ratings || 0
  };





  // Helper function to render icon
  const renderIcon = (iconName, className = "w-6 h-6") => {
    const iconMap = {
      'card-outline': <Star className={className} />,
      'directions-run': <Zap className={className} />,
      'heart-outline': <Heart className={className} />,
      'people-outline': <Users className={className} />,
      'people': <Users className={className} />,
      'zap': <Zap className={className} />,
      'edit': <Edit3 className={className} />,
      'help-circle': <HelpCircle className={className} />,
      'message-circle': <MessageCircle className={className} />,
      'shield': <Shield className={className} />,
      'bell': <Bell className={className} />,
      'log-out': <LogOut className={className} />,
    };
    return iconMap[iconName] || null;
  };

  // Button configuration - similar to mobile version
  const getButtonConfig = () => [
    {
      id: 'clubcard',
      mainButton: {
        icon: 'card-outline',
        label: user?.user_type === 'runclub' ? 'ClubCard' : 'RunCard',
        onPress: () => {
          // For web, open edit modal for runcard/clubcard
          if (user?.user_type === 'runclub') {
            // Open clubcard edit modal
            setClubImageData({ 
              image: getPrimaryImageUrl(clubData?.clubcard_images) || '', 
              description: clubData?.description || '' 
            });
            setClubImagePosition(clubData?.imagePosition || DEFAULT_IMAGE_POSITION);
            setIsEditingClubImage(true);
          } else {
            // Open runcard edit modal
            setRunCardImageData({
              image: user?.runcard_image || '',
              description: user?.bio || '' 
            });
            setIsEditingRunCardImage(true);
          }
        }
      },
      settingsButtons: []
    },
    {
      id: 'profile',
      mainButton: {
        icon: null,
        isProfilePicture: true,
        label: 'Profile Picture',
        onPress: () => setIsEditingProfilePicture(true)
      },
      settingsButtons: [
        {
          icon: 'bookmark-outline',
          label: 'Saved Runs',
          onPress: () => {
            navigate('/mysavedruns');
          }
        },
        ...(user?.user_type === 'runclub' ? [{
          icon: 'people',
          label: 'Connections',
          onPress: () => {
            // Scroll to connections section
            const connectionsSection = document.querySelector('[data-connections]');
            if (connectionsSection) {
              connectionsSection.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }] : []),
        {
          icon: 'edit',
          label: 'Edit Profile',
          onPress: () => {
            if (user?.user_type === 'runclub') {
              setIsEditingClub(true);
            } else {
              // Show edit options modal
              setIsRunnerEditModalOpen(true);
            }
          }
        },
        {
          icon: 'message-circle',
          label: 'Submit Feedback',
          onPress: () => {
            setIsFeedbackModalOpen(true);
          }
        },
        {
          icon: 'log-out',
          label: 'Logout',
          onPress: () => {
            if (window.confirm('Are you sure you want to logout?')) {
              logout();
            }
          }
        }
      ]
    }
  ];

  // Create runner data for RunCard component
  const previewRunner = {
    id: user?.id,
    name: user?.name || 'Runner',
    runcard_image: user?.runcard_image,
    location: formatProfileLocationLabel(user) || user?.city || 'Location not set'
  };

  // Location management functions
  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handleLocationSave = async (location) => {
    try {
      const response = await executeUpdate(
        () => apiService.users.updateLocation({ city: location }),
        { 
          showSuccessToast: true,
          successMessage: 'Location updated successfully!'
        }
      );
      
      // Update user context with new location
      setUser(response);
      setSelectedLocation(location);
      setIsEditingLocation(false);
    } catch (error) {
      // Error is already handled by useApi hook
    }
  };

  const handleLocationCancel = () => {
    setSelectedLocation(user?.city || '');
    setIsEditingLocation(false);
  };

  const startLocationEdit = () => {
    setIsEditingLocation(true);
  };

  const handleLocationSelectAndSave = async (location) => {
    setSelectedLocation(location);
    await handleLocationSave(location);
  };

  const handleLocationRemove = async () => {
    try {
      const response = await executeUpdate(
        () => apiService.users.updateLocation({ city: null }),
        { 
          showSuccessToast: true,
          successMessage: 'Location removed successfully!'
        }
      );
      
      // Update user context with removed location
      setUser(response);
      setSelectedLocation('');
      setIsEditingLocation(false);
    } catch (error) {
      // Error is already handled by useApi hook
    }
  };

  const handleDistanceUnitChange = async (value) => {
    try {
      const response = await apiService.put('/auth/me', { distance_unit: value });
      setUser(response);
      toast.success('Distance unit updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update distance unit');
    }
  };

  const handleResetPassword = () => {
    setIsPasswordModalOpen(true);
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validatePasswordForm = () => {
    const errors = {};
    
    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    
    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else {
      // Use the same validation as the form hook
      const passwordError = validatePassword(passwordData.newPassword, {
        minLength: 8,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true
      });
      if (passwordError) {
        errors.newPassword = passwordError;
      }
    }
    
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (passwordData.currentPassword === passwordData.newPassword) {
      errors.newPassword = 'New password must be different from current password';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      toast.error('Please fix the errors below');
      return;
    }
    
    await executePasswordUpdate(
      () => apiService.users.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }),
      { 
        showSuccessToast: true,
        successMessage: 'Password updated successfully!'
      }
    );
    
    setIsPasswordModalOpen(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordErrors({});
  };

  const handlePhoneUpdate = () => {
    setIsPhoneModalOpen(true);
    setPhoneData({ phone: user?.phone || '' });
  };


  const formatPhoneNumber = (value) => sanitizePhoneInput(value);

  const handlePhoneChange = (field, value) => {
    const formattedValue = field === 'phone' ? formatPhoneNumber(value) : value;
    setPhoneData(prev => ({ ...prev, [field]: formattedValue }));
    if (phoneErrors[field]) {
      setPhoneErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validatePhoneForm = () => {
    const errors = {};
    const phoneError = validatePhone(phoneData.phone);
    if (phoneError) {
      errors.phone = phoneError;
    }
    setPhoneErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!validatePhoneForm()) {
      toast.error('Please fix the errors below');
      return;
    }
    
    await executePhoneUpdate(async () => {
      const response = await apiService.users.updatePhone({ phone: phoneData.phone });
      setUser(response);
      
      // Also update the club's contact phone if user is a run club owner
      if (user.user_type === "runclub" && user.club_id) {
        try {
          const clubUpdateData = {
            name: clubData?.name || 'Club',
            type: clubData?.type || 'Social',
            city: clubData?.city || 'Unknown',
            state: clubData?.state || '',
            description: clubData?.description || 'No description provided',
            image: clubData?.image || 'https://images.unsplash.com/photo-1598014514548-27c9cd1c8466?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxydW5uaW5nJTIwY29tbXVuaXR5fGVufDB8fHx8MTc1ODIxMTI1Mnww&ixlib=rb-4.1.0&q=85',
            members: clubData?.members || 0,
            rating: clubData?.rating || 0.0,
            skill_level: clubData?.skill_level || null,
            contact_phone: phoneData.phone
          };
          
          await executeUpdate(
            () => apiService.clubs.update(user.club_id, clubUpdateData),
            { showSuccessToast: false }
          );
          
          // Refresh club data
          await refetchClub();
        } catch (clubError) {
          // Don't show error to user as the main phone update succeeded
        }
      }
      
      toast.success('Phone number updated successfully!');
      setIsPhoneModalOpen(false);
      setPhoneErrors({});
      return response;
    });
  };

  // Email editing functions
  const validateEmailForm = () => {
    const errors = {};
    if (!emailData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(emailData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    setEmailErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmailForm()) {
      toast.error('Please fix the errors below');
      return;
    }
    
    setEmailUpdateLoading(true);
    try {
      const result = await updateEmail(emailData.email);
      if (result.success) {
        // Also update the club's contact email if user is a run club owner
        if (user.user_type === "runclub" && user.club_id) {
          try {
            const clubUpdateData = {
              name: clubData?.name || 'Club',
              type: clubData?.type || 'Social',
              city: clubData?.city || 'Unknown',
              state: clubData?.state || '',
              description: clubData?.description || 'No description provided',
              image: clubData?.image || 'https://images.unsplash.com/photo-1598014514548-27c9cd1c8466?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxydW5uaW5nJTIwY29tbXVuaXR5fGVufDB8fHx8MTc1ODIxMTI1Mnww&ixlib=rb-4.1.0&q=85',
              members: clubData?.members || 0,
              rating: clubData?.rating || 0.0,
              skill_level: clubData?.skill_level || null,
              contact_email: emailData.email
            };
            
            await executeUpdate(
              () => apiService.clubs.update(user.club_id, clubUpdateData),
              { showSuccessToast: false }
            );
            
            // Refresh club data
            await refetchClub();
          } catch (clubError) {
            // Don't show error to user as the main email update succeeded
          }
        }
        
        toast.success('Email updated successfully!');
        setIsEditingEmail(false);
        setEmailErrors({});
      } else {
        toast.error(result.error || 'Failed to update email');
      }
    } catch (error) {
      toast.error('Failed to update email. Please try again.');
    } finally {
      setEmailUpdateLoading(false);
    }
  };

  // Profile picture editing functions
  const validateProfilePictureForm = () => {
    const errors = {};
    
    // If no file is selected and no URL is provided
    if (!selectedFile && !profilePictureData.profile_picture.trim()) {
      errors.profile_picture = 'Please select an image file or enter an image URL';
    }
    
    // If a file is selected, validate the file
    if (selectedFile) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(selectedFile.type)) {
        errors.profile_picture = 'Please select a valid image file (JPG, PNG, GIF, or WebP)';
      }
      if (selectedFile.size > 20 * 1024 * 1024) { // 20MB limit
        errors.profile_picture = 'Image file size must be less than 20MB';
      }
    }
    
    // If no file is selected but URL is provided, validate the URL
    if (!selectedFile && profilePictureData.profile_picture.trim() && !/^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)$/i.test(profilePictureData.profile_picture)) {
      errors.profile_picture = 'Please enter a valid image URL';
    }
    
    setProfilePictureErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfilePictureSubmit = async (e) => {
    e.preventDefault();
    if (!validateProfilePictureForm()) {
      toast.error('Please fix the errors below');
      return;
    }
    setProfilePictureLoading(true);
    try {
      let imageUrl;
      
      // If a file is selected, upload it to GCP
      if (selectedFile) {
        // Compress image before upload
        const compressedFile = await compressImageForUpload(selectedFile, 'profile');
        const response = await apiService.upload.image(compressedFile, 'profile');
        imageUrl = response.data.url;
      } else {
        // Use the URL from the input field
        imageUrl = profilePictureData.profile_picture;
      }
      
      // Validate that we have a URL
      if (!imageUrl || !imageUrl.trim()) {
        toast.error('Please select an image file or enter an image URL');
        return;
      }
      
      const result = await updateProfilePicture(imageUrl);
      if (result.success) {
        toast.success('Profile picture updated successfully!');
        setIsEditingProfilePicture(false);
        setProfilePictureErrors({});
        setSelectedFile(null);
        setPreviewUrl(null);
      } else {
        toast.error(result.error || 'Failed to update profile picture');
      }
    } catch (error) {
      toast.error('Failed to update profile picture. Please try again.');
    } finally {
      setProfilePictureLoading(false);
    }
  };

  // File handling functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setProfilePictureData({ profile_picture: '' }); // Clear URL input when file is selected
      setProfilePictureErrors({}); // Clear any existing errors when file is selected
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      if (clubImagePreviewUrl) {
        URL.revokeObjectURL(clubImagePreviewUrl);
      }
      if (runCardImagePreviewUrl) {
        URL.revokeObjectURL(runCardImagePreviewUrl);
      }
    };
  }, [previewUrl, clubImagePreviewUrl, runCardImagePreviewUrl]);

  // Club data is now handled by useFetch hook above

  // Club info editing functions
  const handleClubEdit = () => {
    setClubEditData({
      club_type: clubData?.type || '',
      location: clubData?.city ? `${clubData.city}, ${clubData.state}` : '',
      skill_level: clubData?.skill_level || '',
      description: clubData?.description || '',
      home_base_address: clubData?.home_base_address || '',
      home_base_description: clubData?.home_base_description || '',
    });
    setIsEditingClub(true);
  };

  const handleClubUpdate = async (e) => {
    e.preventDefault();
    // Require location: either meet-up address or city/state
    const hasMeetup = (clubEditData.home_base_address || '').trim().length > 0;
    const locationStr = (clubEditData.location || '').trim();
    const locationParts = locationStr.split(',').map(s => s.trim()).filter(Boolean);
    const hasCityState = locationParts.length >= 2; // both city and state required when not using meet-up address
    if (!hasMeetup && !hasCityState) {
      toast.error('Please add a meet-up address or city/state (Location in Basic Information).');
      return;
    }
    try {
      setClubEditLoading(true);

      // Use parsed location (backend overwrites city/state from meet-up when home_base_address is set)
      const city = locationParts[0] || (clubData?.city ?? 'Unknown');
      const state = locationParts[1] || (clubData?.state ?? '');

      const updateData = {
        name: clubData?.name || 'Club',
        type: clubEditData.club_type || 'Social',
        city,
        state,
        description: clubEditData.description || 'No description provided',
        image: clubData?.image || 'https://images.unsplash.com/photo-1598014514548-27c9cd1c8466?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxydW5uaW5nJTIwY29tbXVuaXR5fGVufDB8fHx8MTc1ODIxMTI1Mnww&ixlib=rb-4.1.0&q=85',
        members: clubData?.members || 0,
        rating: clubData?.rating || 0.0,
        skill_level: clubEditData.skill_level || null,
        contact_email: clubData?.contact_email || null,
        contact_phone: clubData?.contact_phone || null,
        home_base_address: clubEditData.home_base_address || null,
        home_base_description: clubEditData.home_base_description || null
      };

      const response = await executeUpdate(
        () => apiService.clubs.update(user.club_id, updateData),
        { showSuccessToast: false }
      );
      
      // Club data is automatically updated by useFetch hook
      setIsEditingClub(false);
      toast.success('Club information updated successfully');
    } catch (error) {
      toast.error('Failed to update club information');
    } finally {
      setClubEditLoading(false);
    }
  };

  const handleClubCancel = () => {
    setIsEditingClub(false);
    setClubEditData({});
  };

  // Club image editing functions
  const validateClubImageForm = () => {
    const errors = {};
    
    // If no file is selected and no URL is provided AND no existing images
    const hasExistingImages = clubData?.clubcard_images && Array.isArray(clubData.clubcard_images) && clubData.clubcard_images.length > 0;
    if (!selectedClubImageFile && !clubImageData.image.trim() && !hasExistingImages) {
      errors.image = 'Please select an image file or enter an image URL';
    }
    
    // If a file is selected, validate the file
    if (selectedClubImageFile) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(selectedClubImageFile.type)) {
        errors.image = 'Please select a valid image file (JPG, PNG, GIF, or WebP)';
      }
      if (selectedClubImageFile.size > 20 * 1024 * 1024) { // 20MB limit
        errors.image = 'Image file size must be less than 20MB';
      }
    }
    
    // If no file is selected but URL is provided, validate the URL
    if (!selectedClubImageFile && clubImageData.image.trim() && !/^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)$/i.test(clubImageData.image)) {
      errors.image = 'Please enter a valid image URL';
    }
    
    // Validate description
    if (!clubImageData.description.trim()) {
      errors.description = 'Please enter a description for your club';
    }
    
    setClubImageErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClubImageSubmit = async (e) => {
    e.preventDefault();
    if (!validateClubImageForm()) {
      toast.error('Please fix the errors below');
      return;
    }
    setClubImageLoading(true);
    
    // Check if user has a club_id
    if (!user.club_id) {
      toast.error('No club found. Please refresh the page and try again.');
      setClubImageLoading(false);
      return;
    }
    
    // Check if clubData exists
    if (!clubData) {
      toast.error('Club data not loaded. Please refresh the page and try again.');
      setClubImageLoading(false);
      return;
    }
    
    // Mobile-specific validation
    if (selectedClubImageFile) {
      // Check if we're on mobile and file might be corrupted
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isMobile && selectedClubImageFile.size === 0) {
        toast.error('The selected image appears to be corrupted. Please try selecting a different image.');
        setClubImageLoading(false);
        return;
      }
    }
    
    try {
      let imageUrl;
      
      // If a file is selected, upload it to GCP
      if (selectedClubImageFile) {
        try {
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          
          // Mobile-specific file validation
          if (isMobile) {
            // Check if file is corrupted or has issues
            if (!selectedClubImageFile.type || selectedClubImageFile.type === '') {
              toast.error('Invalid file type detected. Please try selecting a different image.');
              setClubImageLoading(false);
              return;
            }
            
            // Check if file is too small (might be corrupted)
            if (selectedClubImageFile.size < 1000) { // Less than 1KB
              toast.error('File appears to be corrupted. Please try a different image.');
              setClubImageLoading(false);
              return;
            }
            
            // Check file size for production (current limit is 20MB)
            if (selectedClubImageFile.size > 20 * 1024 * 1024) {
              toast.error('File is too large. Please choose an image smaller than 20MB.');
              setClubImageLoading(false);
              return;
            }
            
            // Check for HEIC files (not supported in production yet)
            if (selectedClubImageFile.type === 'image/heic' || selectedClubImageFile.type === 'image/heif') {
              toast.error('HEIC files are not supported yet. Please convert to JPEG or PNG.');
              setClubImageLoading(false);
              return;
            }
          }
          
          // Compress image before upload
          const compressedFile = await compressImageForUpload(selectedClubImageFile, 'club');
          const response = await apiService.upload.image(compressedFile, 'clubcard_images');
          imageUrl = response.data.url;
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          
          // Handle specific error cases
          if (uploadError.response?.status === 413) {
            toast.error('Image file is too large. Please choose a smaller image.');
          } else if (uploadError.response?.status === 400) {
            toast.error('Invalid image file. Please try a different image.');
          } else if (uploadError.code === 'ECONNABORTED') {
            toast.error('Upload timed out. Please check your connection and try again.');
          } else if (uploadError.response?.status === 500) {
            toast.error('Server error during upload. Please try again in a moment.');
          } else if (!uploadError.response) {
            toast.error('Network error. Please check your connection and try again.');
          } else {
            toast.error('Failed to upload image. Please try again.');
          }
          setClubImageLoading(false);
          return;
        }
      } else {
        // Use the URL from the input field
        imageUrl = clubImageData.image;
      }
      
      // Get existing images or create new array
      const existingImages = clubData?.clubcard_images || [];
      
      // If no new image URL, keep existing images
      let updatedImages = existingImages;
      
      if (imageUrl && imageUrl.trim()) {
        // Create new image object
        const newImage = {
          url: imageUrl,
          order: existingImages.length,
          is_primary: existingImages.length === 0, // First image is primary
        };
        
        // Add new image to array (limit to 5 images)
        updatedImages = [...existingImages, newImage].slice(0, 5);
      } else if (existingImages.length === 0) {
        toast.error('Please select an image file or enter an image URL');
        return;
      }
      
      // PATCH only what this action changes. ClubUpdate is partial, so unsent fields
      // are left untouched — never re-send name/city/etc. with `|| 'Unknown'` fallbacks
      // that would overwrite real club data with placeholders.
      const updateData = {
        clubcard_images: updatedImages,
        imagePosition: convertImagePositionToFloat(clubImagePosition),
      };
      // Only touch the description if the user actually entered one in this modal.
      const editedDescription = clubImageData.description?.trim();
      if (editedDescription) {
        updateData.description = editedDescription;
      }


      // Update club directly without using executeUpdate to avoid any dialogs
      try {
        await apiService.clubs.update(user.club_id, updateData);
      } catch (updateError) {
        // Handle update errors with toast only, no alerts
        if (updateError.response?.status === 403) {
          toast.error('You are not authorized to update this club.');
        } else if (updateError.response?.status === 400) {
          const errorDetail = updateError.response.data?.detail || 'Invalid club data';
          toast.error(`Invalid data: ${errorDetail}`);
        } else if (updateError.response?.status === 404) {
          toast.error('Club not found. Please refresh the page and try again.');
        } else if (updateError.response?.status === 500) {
          toast.error('Server error. Please try again in a moment.');
        } else if (!updateError.response) {
          toast.error('Network error. Please check your connection and try again.');
        } else {
          toast.error('Failed to update clubcard. Please try again.');
        }
        setClubImageLoading(false);
        return;
      }
      
      // Refresh club data
      await refetchClub();
      
      // Show success toast (pill alert style)
      toast.success('Clubcard updated successfully!');
      
      // Close modal after a brief delay to ensure toast is visible
      setTimeout(() => {
        setIsEditingClubImage(false);
      }, 100);
      setClubImageErrors({});
      setSelectedClubImageFile(null);
      setClubImagePreviewUrl(null);
      
      // Reset file input for mobile compatibility
      const fileInput = document.getElementById('club_image_file');
      if (fileInput) {
        fileInput.value = '';
      }
      
      setClubImageLoading(false);
    } catch (error) {
      console.error('Club update error:', error);
      
      // Handle any unexpected errors with toast only
      toast.error('Failed to update clubcard. Please try again.');
      setClubImageLoading(false);
    }
  };

  // Club image file handling functions
  const handleClubImageFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Additional mobile-specific validation
      if (!file.type || !file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }
      
      // Check file size (20MB limit)
      if (file.size > 20 * 1024 * 1024) {
        toast.error('Image file is too large. Please choose an image smaller than 20MB.');
        return;
      }
      
      // Check if file is corrupted or empty
      if (file.size === 0) {
        toast.error('The selected file appears to be empty. Please try a different image.');
        return;
      }
      
      setSelectedClubImageFile(file);
      const url = URL.createObjectURL(file);
      setClubImagePreviewUrl(url);
      setClubImageData(prev => ({ ...prev, image: '' })); // Clear URL input when file is selected
      setClubImageErrors({}); // Clear any existing errors when file is selected
    }
  };

  const handleRemoveClubImageFile = () => {
    setSelectedClubImageFile(null);
    if (clubImagePreviewUrl) {
      URL.revokeObjectURL(clubImagePreviewUrl);
      setClubImagePreviewUrl(null);
    }
    // Reset the file input for mobile compatibility
    const fileInput = document.getElementById('club_image_file');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Club image positioning handlers
  const handleClubImageMouseDown = (e) => {
    if (!isClubImagePositioning) return;
    setIsClubImageDragging(true);
    setClubImageDragStart({
      x: e.clientX - clubImagePosition.x,
      y: e.clientY - clubImagePosition.y
    });
  };

  const handleClubImageMouseMove = (e) => {
    if (!isClubImageDragging || !isClubImagePositioning) return;
    
    const newX = Math.max(0, Math.min(100, ((e.clientX - clubImageDragStart.x) / e.currentTarget.offsetWidth) * 100));
    const newY = Math.max(0, Math.min(100, ((e.clientY - clubImageDragStart.y) / e.currentTarget.offsetHeight) * 100));
    
    setClubImagePosition({ x: newX, y: newY });
  };

  const handleClubImageMouseUp = () => {
    setIsClubImageDragging(false);
  };

  const startClubImagePositioning = () => {
    setIsClubImagePositioning(true);
  };

  const saveClubImagePosition = async () => {
    setIsClubImagePositioning(false);
    
    // Save the position to the backend
    try {
      const updateData = {
        imagePosition: convertImagePositionToFloat(clubImagePosition)
      };
      
      await apiService.clubs.update(user.club_id, updateData);
      
      // Refresh club data to get the updated information
      refetchClub();
      
      toast.success('Image position saved successfully!');
    } catch (error) {
      toast.error('Failed to save image position');
    }
  };

  // Run card image editing functions
  const validateRunCardImageForm = () => {
    const errors = {};
    
    // If no file is selected and no URL is provided
    if (!selectedRunCardImageFile && !runCardImageData.image.trim()) {
      errors.image = 'Please select an image file or enter an image URL';
    }
    
    // If a file is selected, validate the file
    if (selectedRunCardImageFile) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(selectedRunCardImageFile.type)) {
        errors.image = 'Please select a valid image file (JPG, PNG, GIF, or WebP)';
      }
      if (selectedRunCardImageFile.size > 20 * 1024 * 1024) { // 20MB limit
        errors.image = 'Image file size must be less than 20MB';
      }
    }
    
    // If no file is selected but URL is provided, validate the URL
    if (!selectedRunCardImageFile && runCardImageData.image.trim() && !/^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)$/i.test(runCardImageData.image)) {
      errors.image = 'Please enter a valid image URL';
    }
    
    setRunCardImageErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRunCardImageSubmit = async (e) => {
    e.preventDefault();
    if (!validateRunCardImageForm()) {
      toast.error('Please fix the errors below');
      return;
    }
    setRunCardImageLoading(true);
    try {
      let imageUrl;
      
      // If a file is selected, upload it to GCP
      if (selectedRunCardImageFile) {
        try {
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          
          // Mobile-specific file validation
          if (isMobile) {
            // Check if file is corrupted or has issues
            if (!selectedRunCardImageFile.type || selectedRunCardImageFile.type === '') {
              toast.error('File appears to be corrupted. Please try a different image.');
              setRunCardImageLoading(false);
              return;
            }
            
            // Check if file is too small (might be corrupted)
            if (selectedRunCardImageFile.size < 1000) { // Less than 1KB
              toast.error('File appears to be corrupted. Please try a different image.');
              setRunCardImageLoading(false);
              return;
            }
            
            // Check file size for production (current limit is 20MB)
            if (selectedRunCardImageFile.size > 20 * 1024 * 1024) {
              toast.error('File is too large. Please choose an image smaller than 20MB.');
              setRunCardImageLoading(false);
              return;
            }
            
            // Check for HEIC files (not supported in production yet)
            if (selectedRunCardImageFile.type === 'image/heic' || selectedRunCardImageFile.type === 'image/heif') {
              toast.error('HEIC files are not supported yet. Please convert to JPEG or PNG.');
              setRunCardImageLoading(false);
              return;
            }
          }
          
          // Upload original image without compression for better quality
          const response = await apiService.upload.image(selectedRunCardImageFile, 'runcard_image');
          imageUrl = response.data.url;
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          
          // Handle specific error cases
          if (uploadError.response?.status === 413) {
            toast.error('Image file is too large. Please choose a smaller image.');
          } else if (uploadError.response?.status === 400) {
            toast.error('Invalid image file. Please try a different image.');
          } else if (uploadError.code === 'ECONNABORTED') {
            toast.error('Upload timed out. Please check your connection and try again.');
          } else if (uploadError.response?.status === 500) {
            toast.error('Server error during upload. Please try again in a moment.');
          } else if (!uploadError.response) {
            toast.error('Network error. Please check your connection and try again.');
          } else {
            toast.error('Failed to upload image. Please try again.');
          }
          setRunCardImageLoading(false);
          return;
        }
      } else {
        // Use the URL from the input field
        imageUrl = runCardImageData.image;
      }
      
      // Validate that we have a URL
      if (!imageUrl || !imageUrl.trim()) {
        toast.error('Please select an image file or enter an image URL');
        return;
      }
      
      const runcard = await executeUpdate(
        () => apiService.runcards.updateMe({ image: imageUrl }),
        { 
          showSuccessToast: true,
          successMessage: 'Run card image updated successfully!',
          showErrorToast: true
        }
      );
      // Keep auth context in sync (hero lives in runcards; /auth/me merges runcard.image into user.runcard_image)
      const updatedImage = runcard?.image ?? imageUrl;
      setUser((prev) => (prev ? { ...prev, runcard_image: updatedImage } : prev));
      
      setIsEditingRunCardImage(false);
      setRunCardImageErrors({});
      setSelectedRunCardImageFile(null);
      setRunCardImagePreviewUrl(null);
      
      // Reset file input for mobile compatibility
      const fileInput = document.getElementById('run_card_image_file');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      console.error('Error updating run card image:', error);
      // The executeUpdate function already shows error toast, but we can add more specific handling
      if (error?.response?.status === 404) {
        toast.error('API endpoint not found. Please contact support.');
      } else if (error?.response?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else if (!error?.response) {
        toast.error('Network error. Please check your connection.');
      }
      // If executeUpdate already showed an error toast, we don't need to show another one
    } finally {
      setRunCardImageLoading(false);
    }
  };

  // Run card image file handling functions
  const handleRunCardImageFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedRunCardImageFile(file);
      const url = URL.createObjectURL(file);
      setRunCardImagePreviewUrl(url);
      setRunCardImageData(prev => ({ ...prev, image: '' })); // Clear URL input when file is selected
      setRunCardImageErrors({}); // Clear any existing errors when file is selected
    }
  };

  const handleRemoveRunCardImageFile = () => {
    setSelectedRunCardImageFile(null);
    if (runCardImagePreviewUrl) {
      URL.revokeObjectURL(runCardImagePreviewUrl);
      setRunCardImagePreviewUrl(null);
    }
  };


  // Handle viewing connection run card
  const handleViewConnectionRunCard = async (connection) => {
    setSelectedConnection(connection);
    setShowRunCard(true);
    setLoadingRunCard(true);
    
    try {
      // Fetch the connection's full profile data
      const connectionProfile = await apiService.users.getProfile(connection.runner_id);
      setSelectedConnection(connectionProfile);
    } catch (error) {
      toast.error('Failed to load connection profile');
    } finally {
      setLoadingRunCard(false);
    }
  };

  const handleBackToConnections = () => {
    setShowRunCard(false);
    setSelectedConnection(null);
  };



  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center page-gradient">
        <div className="text-runcards-text-primary font-runcards">Loading...</div>
      </div>
    );
  }

  // If no user after loading, show nothing (redirect handled in useEffect)
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-runcards-background pwa-content">
      <Navigation />
      
      {/* Modern Header */}
      <div className="px-4 py-6 pt-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-runcards-xxl font-runcards-bold text-runcards-text-primary font-runcards-bold">
              Profile
            </h1>
            <Button
              onClick={() => navigate('/messages')}
              variant="ghost"
              className="p-2 hover:bg-runcards-foreground/50 rounded-lg transition-colors flex-shrink-0"
              style={{ color: UI.COLORS.TEXT_PRIMARY }}
              title="Messages"
            >
              <MessageSquare size={24} />
            </Button>
          </div>
          <p className="text-runcards-text-secondary text-runcards-md font-runcards mb-6">
            {user.user_type === "runclub" 
              ? "Manage your run club and connections" 
              : "Manage your running activities"
            }
          </p>

          {/* Clubcard Status - Based on is_public field */}
          {user?.user_type === 'runclub' && clubData && (
            <div className="mb-4">
              <div className={`inline-flex items-center px-3 py-2 rounded-lg text-runcards-sm font-runcards ${
                clubData.is_public
                  ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                  : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
              }`}>
                <Star className="w-4 h-4 mr-2" />
                {clubData.is_public
                  ? 'Clubcard Live' 
                  : 'Clubcard Private'
                }
              </div>
              {!clubData.is_public && (
                <div className="mt-2">
                  <Button
                    onClick={() => {
                      // Open clubcard edit modal
                      setClubImageData({ 
                        image: getPrimaryImageUrl(clubData?.clubcard_images) || '', 
                        description: clubData?.description || '' 
                      });
                      setClubImagePosition(clubData?.imagePosition || DEFAULT_IMAGE_POSITION);
                      setIsEditingClubImage(true);
                    }}
                    className="bg-yellow-500 text-black hover:bg-yellow-400 text-runcards-xs px-3 py-1 font-runcards"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Add Image to Go Public
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-8 overflow-x-hidden">
        <div className="max-w-7xl mx-auto">

        {user.user_type === "runclub" ? (
          /* Run Club Profile */
          <div className="max-w-4xl mx-auto">
            {/* Profile Information Card - Mobile Style */}
            <Card className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl mb-6">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {/* Profile Image */}
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 bg-white/10 border border-runcards-border/20 rounded-full flex items-center justify-center overflow-hidden">
                      {previewUrl ? (
                        <img 
                          src={previewUrl} 
                          alt="Profile Preview" 
                          className="w-full h-full object-cover"
                        />
                      ) : user.profile_picture ? (
                        <img 
                          src={user.profile_picture} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Building2 className="w-8 h-8 text-runcards-text-primary" />
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingProfilePicture(true)}
                      className="absolute -bottom-1 -right-1 bg-runcards-text-primary text-runcards-background hover:bg-runcards-text-primary/90 p-1 h-6 w-6 rounded-full"
                    >
                      <Camera className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  {/* Profile Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-runcards-lg font-runcards-bold text-runcards-text-primary mb-1">
                      {clubData?.name || user.name || "Run Club"}
                    </h3>
                    <div className="space-y-1">
                      <div className="flex items-center text-runcards-text-secondary text-runcards-sm">
                        <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate font-runcards">{user.email || 'No email set'}</span>
                      </div>
                      <div className="flex items-center text-runcards-text-secondary text-runcards-sm">
                        <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="font-runcards">
                          {user.phone ? formatPhoneNumberDisplay(user.phone) : 'No phone set'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Floating Rows - Mobile Style */}
            <div className="space-y-2 mb-6">
              {getButtonConfig().map((column) => (
                <div key={column.id} className="space-y-2">
                  {/* Main Action Row - Skip profile picture rows */}
                  {!column.mainButton.isProfilePicture && (
                    <button
                      onClick={column.mainButton.onPress}
                      className="w-full flex items-center px-4 py-3 bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-xl text-runcards-text-primary hover:bg-white/5 transition-all duration-200"
                    >
                      <div className="w-6 h-6 mr-3 flex items-center justify-center">
                        {column.mainButton.icon && renderIcon(column.mainButton.icon, "w-6 h-6")}
                      </div>
                      <span className="font-runcards font-medium flex-1 text-left">{column.mainButton.label}</span>
                    </button>
                  )}
                  {/* Distance unit preference - profile column only */}
                  {column.id === 'profile' && (
                    <div className="w-full flex items-center px-4 py-3 bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-xl text-runcards-text-primary mb-2">
                      <span className="font-runcards flex-1 text-left">Distance unit</span>
                      <Select
                        value={user?.distance_unit || 'miles'}
                        onValueChange={handleDistanceUnitChange}
                      >
                        <SelectTrigger className="w-[120px] bg-white/10 border-runcards-border/20 text-runcards-text-primary">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="miles">Miles</SelectItem>
                          <SelectItem value="kilometers">Kilometers</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {/* Settings Rows - Always visible like mobile */}
                  {column.settingsButtons.map((settingsButton, buttonIndex) => (
                    settingsButton.isToggle ? (
                      <div
                        key={`${column.id}-${buttonIndex}`}
                        className="w-full flex items-center px-4 py-3 bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-xl text-runcards-text-primary"
                      >
                        <div className="w-6 h-6 mr-3 flex items-center justify-center">
                          {settingsButton.icon && renderIcon(settingsButton.icon, "w-5 h-5")}
                        </div>
                        <span className="font-runcards flex-1 text-left">{settingsButton.label}</span>
                        <input
                          type="checkbox"
                          checked={settingsButton.toggleValue}
                          onChange={(e) => settingsButton.onToggle(e.target.checked)}
                          className="w-5 h-5"
                        />
                      </div>
                    ) : (
                      <button
                        key={`${column.id}-${buttonIndex}`}
                        onClick={settingsButton.onPress}
                        className="w-full flex items-center px-4 py-3 bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-xl text-runcards-text-primary hover:bg-white/5 transition-all duration-200"
                      >
                        <div className="w-6 h-6 mr-3 flex items-center justify-center">
                          {settingsButton.icon && renderIcon(settingsButton.icon, "w-5 h-5")}
                        </div>
                        <span className="font-runcards flex-1 text-left">{settingsButton.label}</span>
                      </button>
                    )
                  ))}
                </div>
              ))}
            </div>

            {/* Club Details Card */}
            {clubData && (
              <div>
                <Card className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-runcards-text-primary flex items-center justify-between font-runcards-bold">
                      <div className="flex items-center">
                        <Building2 className="w-5 h-5 mr-2" />
                        Club Details
                      </div>
                      {!isEditingClub && (
                        <div className="flex gap-2">
                          <Button
                            onClick={handleClubEdit}
                            variant="outline"
                            size="sm"
                            className="border-runcards-border/40 text-runcards-text-primary/80 hover:bg-white/10 hover:text-runcards-text-primary"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                        </div>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    {isEditingClub ? (
                      <form onSubmit={handleClubUpdate} className="space-y-3">
                        {/* Basic Information */}
                        <div className="bg-white/5 rounded-lg p-3 border border-runcards-border/10">
                          <h4 className="text-runcards-text-primary font-runcards-bold mb-2 flex items-center font-runcards-bold">
                            <Settings className="w-4 h-4 mr-2" />
                            Basic Information
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-runcards-text-secondary text-runcards-sm mb-1 font-runcards">Club Type</Label>
                              <Select
                                value={clubEditData.club_type}
                                onValueChange={(value) => setClubEditData(prev => ({ ...prev, club_type: value }))}
                              >
                                <SelectTrigger className="bg-white/10 border-runcards-border/20 text-runcards-text-primary">
                                  <SelectValue placeholder="Select club type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {CLUB_TYPES.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-runcards-text-secondary text-runcards-sm mb-1 font-runcards">Location</Label>
                              <Input
                                value={clubEditData.location}
                                onChange={(e) => setClubEditData(prev => ({ ...prev, location: e.target.value }))}
                                placeholder="City, State"
                                className="bg-white/10 border-runcards-border/20 text-runcards-text-primary"
                              />
                            </div>
                            <div>
                              <Label className="text-runcards-text-secondary text-runcards-sm mb-1 font-runcards">Who is this club for?</Label>
                              <Select
                                value={clubEditData.skill_level}
                                onValueChange={(value) => setClubEditData(prev => ({ ...prev, skill_level: value }))}
                              >
                                <SelectTrigger className="bg-white/10 border-runcards-border/20 text-runcards-text-primary">
                                  <SelectValue placeholder="Select who this club is for" />
                                </SelectTrigger>
                                <SelectContent>
                                  {SKILL_LEVELS.map((level) => (
                                    <SelectItem key={level.value} value={level.value}>
                                      {level.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        {/* Club Personality */}
                        <div className="bg-white/5 rounded-lg p-3 border border-runcards-border/10">
                          <h4 className="text-runcards-text-primary font-runcards-bold mb-2 flex items-center font-runcards-bold">
                            <Users className="w-4 h-4 mr-2" />
                            Club Personality
                          </h4>
                          <div className="space-y-3">
                            <div>
                              <Label className="text-runcards-text-secondary text-runcards-sm mb-1 font-runcards">Description</Label>
                              <Textarea
                                value={clubEditData.description}
                                onChange={(e) => setClubEditData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Describe your club"
                                className="bg-white/10 border-runcards-border/20 text-runcards-text-primary"
                                rows={3}
                              />
                            </div>
                            <div>
                              <p className="text-runcards-text-secondary text-runcards-xs mb-1 font-runcards">Required: meet-up address below or Location (City, State) in Basic Information.</p>
                              <Label className="text-runcards-text-secondary text-runcards-sm mb-1 font-runcards">
                                Meet Up Spot Address
                                {clubEditData.home_base_address && clubEditData.home_base_address.length > 190 && (
                                  <span className="text-yellow-400 ml-1">
                                    ({clubEditData.home_base_address.length}/200)
                                  </span>
                                )}
                              </Label>
                              <input
                                type="text"
                                value={clubEditData.home_base_address || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 200) {
                                    setClubEditData(prev => ({ ...prev, home_base_address: value }));
                                  }
                                }}
                                placeholder="Address (e.g., 2300 South Dixie Highway, Miami, FL 33133)"
                                className="w-full px-3 py-2 bg-white/10 border border-runcards-border/20 rounded-md text-runcards-text-primary placeholder:text-runcards-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-runcards-accent"
                                maxLength={200}
                              />
                            </div>
                            <div>
                              <Label className="text-runcards-text-secondary text-runcards-sm mb-1 font-runcards">
                                Meet Up Spot Description (Optional)
                                {clubEditData.home_base_description && clubEditData.home_base_description.length > 490 && (
                                  <span className="text-yellow-400 ml-1">
                                    ({clubEditData.home_base_description.length}/500)
                                  </span>
                                )}
                              </Label>
                              <Textarea
                                value={clubEditData.home_base_description || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 500) {
                                    setClubEditData(prev => ({ ...prev, home_base_description: value }));
                                  }
                                }}
                                placeholder="Optional description (e.g., All runs start and end at our shop. Come early, hang after— this is our meeting point.)"
                                className="bg-white/10 border-runcards-border/20 text-runcards-text-primary"
                                rows={2}
                                maxLength={500}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-2 pt-2">
                          <Button
                            type="button"
                            onClick={handleClubCancel}
                            variant="outline"
                            className="border-gray-500 text-runcards-text-secondary hover:bg-gray-600"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={clubEditLoading}
                            className="bg-white text-black hover:bg-gray-100"
                          >
                            {clubEditLoading ? 'Updating...' : 'Update Club'}
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <div className="space-y-3">
                        {/* Basic Information */}
                        <div className="bg-white/5 rounded-lg p-3 border border-runcards-border/10">
                          <h4 className="text-runcards-text-primary font-runcards-bold mb-2 flex items-center font-runcards-bold">
                            <Settings className="w-4 h-4 mr-2" />
                            Basic Information
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-runcards-text-secondary text-runcards-sm font-runcards">Club Type</label>
                              <div className="text-runcards-text-primary font-runcards mt-0.5">{clubData.type || 'Not specified'}</div>
                            </div>
                            <div>
                              <label className="text-runcards-text-secondary text-runcards-sm font-runcards">Location</label>
                              <div className="text-runcards-text-primary font-runcards mt-0.5">{clubData.city && clubData.state ? `${clubData.city}, ${clubData.state}` : 'Not specified'}</div>
                            </div>
                            <div>
                              <label className="text-runcards-text-secondary text-runcards-sm font-runcards">Who is this club for?</label>
                              <div className="text-runcards-text-primary font-runcards mt-0.5">{clubData.skill_level || 'Not specified'}</div>
                            </div>
                          </div>
                        </div>

                        {/* Club Personality */}
                        <div className="bg-white/5 rounded-lg p-3 border border-runcards-border/10">
                          <h4 className="text-runcards-text-primary font-runcards-bold mb-2 flex items-center font-runcards-bold">
                            <Users className="w-4 h-4 mr-2" />
                            Club Personality
                          </h4>
                          <div className="space-y-3">
                            {clubData.description && (
                              <div>
                                <label className="text-runcards-text-secondary text-runcards-sm font-runcards">Description</label>
                                <div className="text-runcards-text-primary font-runcards mt-0.5">{clubData.description}</div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Connections Section */}
                        <div className="bg-white/5 rounded-lg p-3 border border-runcards-border/10">
                          <h4 className="text-runcards-text-primary font-runcards-bold mb-2 flex items-center font-runcards-bold">
                            <Users className="w-4 h-4 mr-2" />
                            Members ({clubData.members || 0})
                          </h4>
                          
                          {clubMembersLoading ? (
                            <div className="text-runcards-text-primary/60 text-center py-2 font-runcards">
                              Loading members...
                            </div>
                          ) : clubMembers && clubMembers.length > 0 ? (
                            <div className="space-y-2">
                              {clubMembers.map((member) => (
                                <div key={member.id} className="flex items-center justify-between p-2 bg-white/5 rounded-lg border border-runcards-border/10 hover:bg-white/10 transition-colors">
                                  <div className="flex items-center flex-1">
                                    {/* Member Profile Picture */}
                                    {member.profile_picture && (
                                      <div className="w-10 h-10 rounded-lg overflow-hidden mr-2 flex-shrink-0">
                                        <img 
                                          src={member.profile_picture} 
                                          alt={member.name || 'Member'}
                                          className="w-full h-full object-cover"
                                        />
                                      </div>
                                    )}
                                    
                                    {/* Member Info */}
                                    <div className="flex-1">
                                      <div className="text-runcards-text-primary font-runcards-bold font-runcards">
                                        {member.name || 'Unknown Member'}
                                      </div>
                                      <div className="text-runcards-text-primary/60 text-runcards-sm font-runcards">
                                        Joined {member.connected_at ? new Date(member.connected_at).toLocaleDateString('en-US', { 
                                          month: 'short', 
                                          day: 'numeric',
                                          year: 'numeric'
                                        }) : 'Recently'}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* View Run Card Button */}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-runcards-border/20 text-runcards-text-primary hover:bg-white/10"
                                    onClick={() => handleViewConnectionRunCard(connection)}
                                  >
                                    View Card
                                  </Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6">
                              <Users className="w-8 h-8 text-runcards-text-primary/40 mx-auto mb-2" />
                              <div className="text-runcards-text-primary/60 font-runcards">
                                No connections yet
                              </div>
                              <div className="text-runcards-text-primary/40 text-runcards-sm font-runcards">
                                Connections will appear here when they connect with your club
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* No Club ID Message */}
            {user?.user_type === 'runclub' && !user?.club_id && (
              <div>
                <Card className="bg-yellow-500/10 border-yellow-500/30 backdrop-blur-xl border rounded-2xl">
                  <CardHeader>
                    <CardTitle className="text-yellow-300 flex items-center font-runcards-bold">
                      <AlertCircle className="w-5 h-5 mr-2" />
                      No Club Found
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <div className="text-yellow-300 text-runcards-lg font-runcards-bold mb-2 font-runcards">
                        ⚠️ No club associated with your account
                      </div>
                      <p className="text-yellow-200/80 text-runcards-sm mb-4 font-runcards">
                        Please complete your run club setup to create your club.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}


          </div>
        ) : (
          /* Runner Profile */
          <div className="max-w-4xl mx-auto">
            {/* Profile Information Card - Mobile Style */}
            <Card className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl mb-6">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {/* Profile Image */}
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 bg-white/10 border border-runcards-border/20 rounded-full flex items-center justify-center overflow-hidden">
                      {previewUrl ? (
                        <img 
                          src={previewUrl} 
                          alt="Profile Preview" 
                          className="w-full h-full object-cover"
                        />
                      ) : user.profile_picture ? (
                        <img 
                          src={user.profile_picture} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-8 h-8 text-runcards-text-primary" />
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingProfilePicture(true)}
                      className="absolute -bottom-1 -right-1 bg-runcards-text-primary text-runcards-background hover:bg-runcards-text-primary/90 p-1 h-6 w-6 rounded-full"
                    >
                      <Camera className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  {/* Profile Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-runcards-lg font-runcards-bold text-runcards-text-primary mb-1">
                      {user.name || 'No name set'}
                    </h3>
                    <div className="space-y-1">
                      <div className="flex items-center text-runcards-text-secondary text-runcards-sm">
                        <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate font-runcards">{user.email || 'No email set'}</span>
                      </div>
                      <div className="flex items-center text-runcards-text-secondary text-runcards-sm">
                        <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="font-runcards">
                          {user.phone ? formatPhoneNumberDisplay(user.phone) : 'No phone set'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Strava Integration - runners only */}
            <Card className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl mb-6">
              <CardContent className="p-6">
                <StravaConnection />
              </CardContent>
            </Card>

            {/* Floating Rows - Mobile Style */}
            <div className="space-y-2 mb-6">
              {getButtonConfig().map((column) => (
                <div key={column.id} className="space-y-2">
                  {/* Main Action Row - Skip profile picture rows */}
                  {!column.mainButton.isProfilePicture && (
                    <button
                      onClick={column.mainButton.onPress}
                      className="w-full flex items-center px-4 py-3 bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-xl text-runcards-text-primary hover:bg-white/5 transition-all duration-200"
                    >
                      <div className="w-6 h-6 mr-3 flex items-center justify-center">
                        {column.mainButton.icon && renderIcon(column.mainButton.icon, "w-6 h-6")}
                      </div>
                      <span className="font-runcards font-medium flex-1 text-left">{column.mainButton.label}</span>
                    </button>
                  )}
                  {/* Distance unit preference - profile column only */}
                  {column.id === 'profile' && (
                    <div className="w-full flex items-center px-4 py-3 bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-xl text-runcards-text-primary mb-2">
                      <span className="font-runcards flex-1 text-left">Distance unit</span>
                      <Select
                        value={user?.distance_unit || 'miles'}
                        onValueChange={handleDistanceUnitChange}
                      >
                        <SelectTrigger className="w-[120px] bg-white/10 border-runcards-border/20 text-runcards-text-primary">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="miles">Miles</SelectItem>
                          <SelectItem value="kilometers">Kilometers</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {/* Settings Rows - Always visible like mobile */}
                  {column.settingsButtons.map((settingsButton, buttonIndex) => (
                    settingsButton.isToggle ? (
                      <div
                        key={`${column.id}-${buttonIndex}`}
                        className="w-full flex items-center px-4 py-3 bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-xl text-runcards-text-primary"
                      >
                        <div className="w-6 h-6 mr-3 flex items-center justify-center">
                          {settingsButton.icon && renderIcon(settingsButton.icon, "w-5 h-5")}
                        </div>
                        <span className="font-runcards flex-1 text-left">{settingsButton.label}</span>
                        <input
                          type="checkbox"
                          checked={settingsButton.toggleValue}
                          onChange={(e) => settingsButton.onToggle(e.target.checked)}
                          className="w-5 h-5"
                        />
                      </div>
                    ) : (
                      <button
                        key={`${column.id}-${buttonIndex}`}
                        onClick={settingsButton.onPress}
                        className="w-full flex items-center px-4 py-3 bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-xl text-runcards-text-primary hover:bg-white/5 transition-all duration-200"
                      >
                        <div className="w-6 h-6 mr-3 flex items-center justify-center">
                          {settingsButton.icon && renderIcon(settingsButton.icon, "w-5 h-5")}
                        </div>
                        <span className="font-runcards flex-1 text-left">{settingsButton.label}</span>
                      </button>
                    )
                  ))}
                </div>
              ))}
            </div>

        </div>
        )}
        </div>
      </div>

      {/* Password Change Modal */}
      <Dialog open={isPasswordModalOpen} onOpenChange={(open) => {
        setIsPasswordModalOpen(open);
        if (!open) {
          setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          setPasswordErrors({});
        }
      }}>
        <DialogContent className="bg-runcards-background backdrop-blur-xl border border-runcards-border/20 max-w-md rounded-2xl p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-runcards-text-primary text-runcards-xl font-runcards-bold flex items-center">
              <Key className="w-5 h-5 mr-2" />
              Change Password
            </DialogTitle>
            <DialogDescription className="text-runcards-text-secondary text-runcards-sm font-runcards mt-2">
              Update your account password. Make sure to use a strong password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <Label htmlFor="current-password" className="text-runcards-text-primary font-runcards">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                className={`bg-runcards-foreground/50 border-runcards-border/20 text-runcards-text-primary ${
                  passwordErrors.currentPassword ? 'border-red-400' : 'focus:border-runcards-accent/50'
                }`}
                placeholder="Enter your current password"
              />
              {passwordErrors.currentPassword && (
                <p className="text-red-400 text-runcards-sm mt-1">{passwordErrors.currentPassword}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="new-password" className="text-runcards-text-primary font-runcards">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                className={`bg-runcards-foreground/50 border-runcards-border/20 text-runcards-text-primary ${
                  passwordErrors.newPassword ? 'border-red-400' : 'focus:border-runcards-accent/50'
                }`}
                placeholder="Enter your new password"
              />
              {passwordErrors.newPassword && (
                <p className="text-red-400 text-runcards-sm mt-1">{passwordErrors.newPassword}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="confirm-password" className="text-runcards-text-primary font-runcards">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                className={`bg-runcards-foreground/50 border-runcards-border/20 text-runcards-text-primary ${
                  passwordErrors.confirmPassword ? 'border-red-400' : 'focus:border-runcards-accent/50'
                }`}
                placeholder="Confirm your new password"
              />
              {passwordErrors.confirmPassword && (
                <p className="text-red-400 text-runcards-sm mt-1">{passwordErrors.confirmPassword}</p>
              )}
            </div>
            
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsPasswordModalOpen(false);
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                  setPasswordErrors({});
                }}
                className="flex-1 border-runcards-border/20 text-runcards-text-primary hover:bg-runcards-foreground/50 hover:border-runcards-border/40 font-runcards font-runcards-bold rounded-runcards-lg"
                disabled={passwordLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-runcards-accent text-runcards-text-primary hover:bg-runcards-accent/80 font-runcards font-runcards-bold rounded-runcards-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={passwordLoading}
              >
                {passwordLoading ? 'Updating...' : 'Update Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Phone Update Modal */}
      <Dialog open={isPhoneModalOpen} onOpenChange={(open) => {
        setIsPhoneModalOpen(open);
        if (!open) {
          setPhoneData({ phone: user?.phone || '' });
          setPhoneErrors({});
        }
      }}>
        <DialogContent className="bg-runcards-background backdrop-blur-xl border border-runcards-border/20 max-w-md rounded-2xl p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-runcards-text-primary text-runcards-xl font-runcards-bold flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Update Phone Number
            </DialogTitle>
            <DialogDescription className="text-runcards-text-secondary text-runcards-sm font-runcards mt-2">
              Add or update your phone number for better communication with running clubs.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePhoneSubmit} className="space-y-4">
            <div>
              <Label htmlFor="phone" className="text-runcards-text-primary font-runcards">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={phoneData.phone}
                onChange={(e) => handlePhoneChange('phone', e.target.value)}
                className={`bg-runcards-foreground/50 border-runcards-border/20 text-runcards-text-primary ${
                  phoneErrors.phone ? 'border-red-400' : 'focus:border-runcards-accent/50'
                }`}
                placeholder="+1 555 123 4567"
                maxLength={14}
              />
              {phoneErrors.phone && (
                <p className="text-red-400 text-runcards-sm mt-1">{phoneErrors.phone}</p>
              )}
            </div>
            
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsPhoneModalOpen(false);
                  setPhoneData({ phone: user?.phone || '' });
                  setPhoneErrors({});
                }}
                className="flex-1 border-runcards-border/20 text-runcards-text-primary hover:bg-runcards-foreground/50 hover:border-runcards-border/40 font-runcards font-runcards-bold rounded-runcards-lg"
                disabled={phoneLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-runcards-accent text-runcards-text-primary hover:bg-runcards-accent/80 font-runcards font-runcards-bold rounded-runcards-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={phoneLoading}
              >
                {phoneLoading ? 'Updating...' : 'Update Phone'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Update Modal */}
      <Dialog open={isEditingEmail} onOpenChange={(open) => {
        setIsEditingEmail(open);
        if (!open) {
          setEmailData({ email: user?.email || '' });
          setEmailErrors({});
        }
      }}>
        <DialogContent className="bg-runcards-background backdrop-blur-xl border border-runcards-border/20 max-w-md rounded-2xl p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-runcards-text-primary text-runcards-xl font-runcards-bold flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              Update Email
            </DialogTitle>
            <DialogDescription className="text-runcards-text-secondary text-runcards-sm font-runcards mt-2">
              Update your email address for account notifications and communication.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-runcards-text-primary font-runcards">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={emailData.email}
                onChange={(e) => setEmailData({ email: e.target.value })}
                className={`bg-runcards-foreground/50 border-runcards-border/20 text-runcards-text-primary ${
                  emailErrors.email ? 'border-red-400' : 'focus:border-runcards-accent/50'
                }`}
                placeholder="your@email.com"
              />
              {emailErrors.email && (
                <p className="text-red-400 text-runcards-sm mt-1">{emailErrors.email}</p>
              )}
            </div>
            
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditingEmail(false);
                  setEmailData({ email: user?.email || '' });
                  setEmailErrors({});
                }}
                className="flex-1 border-runcards-border/20 text-runcards-text-primary hover:bg-runcards-foreground/50 hover:border-runcards-border/40 font-runcards font-runcards-bold rounded-runcards-lg"
                disabled={emailUpdateLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-runcards-accent text-runcards-text-primary hover:bg-runcards-accent/80 font-runcards font-runcards-bold rounded-runcards-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={emailUpdateLoading}
              >
                {emailUpdateLoading ? 'Updating...' : 'Update Email'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Profile Picture Update Modal */}
      <Dialog open={isEditingProfilePicture} onOpenChange={setIsEditingProfilePicture}>
        <DialogContent className="bg-runcards-background/90 backdrop-blur-xl border-runcards-border/20 max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-runcards-text-primary flex items-center font-runcards-bold">
              <User className="w-5 h-5 mr-2" />
              Update Profile Picture
            </DialogTitle>
            <DialogDescription className="text-runcards-text-secondary">
              Upload a new profile picture or enter an image URL.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleProfilePictureSubmit} className="space-y-4">
            {/* File Upload Section */}
            <div>
              <Label htmlFor="profile_file" className="text-runcards-text-primary font-runcards">Upload Image File</Label>
              <Input
                id="profile_file"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleFileSelect}
                className="bg-white/10 border-runcards-border/20 text-runcards-text-primary file:bg-white/20 file:text-runcards-text-primary file:border-0 file:rounded-md file:px-3 file:py-1 file:mr-3 focus:outline-none focus:ring-0 focus:border-runcards-border/20 hover:border-runcards-border/20"
                style={{ 
                  outline: 'none',
                  boxShadow: 'none',
                  borderColor: 'rgba(255, 255, 255, 0.2)'
                }}
              />
              {selectedFile && (
                <div className="mt-2 flex items-center space-x-2">
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="w-16 h-16 rounded-full object-cover border border-runcards-border/20"
                  />
                  <div className="flex-1">
                    <p className="text-runcards-text-primary text-runcards-sm">{selectedFile.name}</p>
                    <p className="text-runcards-text-secondary text-runcards-xs">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveFile}
                    className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-400/50"
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-runcards-border/20"></div>
              </div>
              <div className="relative flex justify-center text-runcards-sm">
                <span className="px-2 bg-runcards-background/90 text-runcards-text-secondary">OR</span>
              </div>
            </div>

            {/* URL Input Section */}
            <div>
              <Label htmlFor="profile_picture" className="text-runcards-text-primary font-runcards">Image URL</Label>
              <Input
                id="profile_picture"
                type="url"
                value={profilePictureData.profile_picture}
                onChange={(e) => {
                  setProfilePictureData({ profile_picture: e.target.value });
                  if (selectedFile) {
                    handleRemoveFile();
                  }
                  // Clear errors when user starts typing in URL field
                  if (e.target.value.trim()) {
                    setProfilePictureErrors({});
                  }
                }}
                className={`bg-white/10 border-runcards-border/20 text-runcards-text-primary ${
                  profilePictureErrors.profile_picture ? 'border-red-400' : 'focus:border-runcards-border/60'
                }`}
                placeholder="https://example.com/your-image.jpg"
              />
              {profilePictureErrors.profile_picture && (
                <p className="text-red-400 text-runcards-sm mt-1">{profilePictureErrors.profile_picture}</p>
              )}
            </div>
            
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditingProfilePicture(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-runcards-text-primary border border-runcards-border/20"
                disabled={profilePictureLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-white text-black hover:bg-gray-100"
                disabled={profilePictureLoading}
              >
                {profilePictureLoading ? 'Updating...' : 'Update Picture'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Club Image Update Modal */}
      <Dialog open={isEditingClubImage} onOpenChange={setIsEditingClubImage}>
        <DialogContent className="bg-runcards-background/90 backdrop-blur-xl border-runcards-border/20 max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-runcards-text-primary flex items-center font-runcards-bold">
              <Building2 className="w-5 h-5 mr-2" />
              Edit Clubcard Preview
            </DialogTitle>
            <DialogDescription className="text-runcards-text-secondary">
              Update your club's image and description to change how it appears to connections.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleClubImageSubmit} className="space-y-4">
            {/* File Upload Section */}
            <div>
              <Label htmlFor="club_image_file" className="text-runcards-text-primary font-runcards">Upload Image File</Label>
              <Input
                id="club_image_file"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif"
                onChange={handleClubImageFileSelect}
                className="bg-white/10 border-runcards-border/20 text-runcards-text-primary file:bg-white/20 file:text-runcards-text-primary file:border-0 file:rounded-md file:px-3 file:py-1 file:mr-3 focus:outline-none focus:ring-0 focus:border-runcards-border/20 hover:border-runcards-border/20"
                style={{ 
                  outline: 'none',
                  boxShadow: 'none',
                  borderColor: 'rgba(255, 255, 255, 0.2)'
                }}
                multiple={false}
              />
              {selectedClubImageFile && (
                <div className="mt-2 flex items-center space-x-2">
                  <img 
                    src={clubImagePreviewUrl} 
                    alt="Preview" 
                    className="w-16 h-16 rounded-lg object-cover border border-runcards-border/20"
                  />
                  <div className="flex-1">
                    <p className="text-runcards-text-primary text-runcards-sm">{selectedClubImageFile.name}</p>
                    <p className="text-runcards-text-secondary text-runcards-xs">{(selectedClubImageFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveClubImageFile}
                    className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-400/50"
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-runcards-border/20"></div>
              </div>
              <div className="relative flex justify-center text-runcards-sm">
                <span className="px-2 bg-runcards-background/90 text-runcards-text-secondary">OR</span>
              </div>
            </div>

            {/* URL Input Section */}
            <div>
              <Label htmlFor="club_image" className="text-runcards-text-primary font-runcards">Image URL</Label>
              <Input
                id="club_image"
                type="url"
                value={clubImageData.image}
                onChange={(e) => {
                  setClubImageData(prev => ({ ...prev, image: e.target.value }));
                  if (selectedClubImageFile) {
                    handleRemoveClubImageFile();
                  }
                  // Clear errors when user starts typing in URL field
                  if (e.target.value.trim()) {
                    setClubImageErrors({});
                  }
                }}
                className={`bg-white/10 border-runcards-border/20 text-runcards-text-primary ${
                  clubImageErrors.image ? 'border-red-400' : 'focus:border-runcards-border/60'
                }`}
                placeholder="https://example.com/your-image.jpg"
              />
              {clubImageErrors.image && (
                <p className="text-red-400 text-runcards-sm mt-1">{clubImageErrors.image}</p>
              )}
            </div>

            {/* Description Section */}
            <div>
              <Label htmlFor="club_description" className="text-runcards-text-primary font-runcards">Club Description</Label>
              <Textarea
                id="club_description"
                value={clubImageData.description}
                onChange={(e) => {
                  setClubImageData(prev => ({ ...prev, description: e.target.value }));
                  // Clear errors when user starts typing
                  if (e.target.value.trim()) {
                    setClubImageErrors(prev => ({ ...prev, description: '' }));
                  }
                }}
                className={`bg-white/10 border-runcards-border/20 text-runcards-text-primary ${
                  clubImageErrors.description ? 'border-red-400' : 'focus:border-runcards-border/60'
                }`}
                placeholder="Describe your running club..."
                rows={3}
              />
              {clubImageErrors.description && (
                <p className="text-red-400 text-runcards-sm mt-1">{clubImageErrors.description}</p>
              )}
            </div>

            
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditingClubImage(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-runcards-text-primary border border-runcards-border/20"
                disabled={clubImageLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-white text-black hover:bg-gray-100"
                disabled={clubImageLoading}
              >
                {clubImageLoading ? 'Updating...' : 'Update Image'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Run Card Image Update Modal */}
      <Dialog open={isEditingRunCardImage} onOpenChange={setIsEditingRunCardImage}>
        <DialogContent className="bg-runcards-background/90 backdrop-blur-xl border-runcards-border/20 max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-runcards-text-primary flex items-center font-runcards-bold">
              <User className="w-5 h-5 mr-2" />
              Edit Run Card
            </DialogTitle>
            <DialogDescription className="text-runcards-text-secondary">
              Update your run card image to change how it appears to other users.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRunCardImageSubmit} className="space-y-4">
            {/* File Upload Section */}
            <div>
              <Label htmlFor="run_card_image_file" className="text-runcards-text-primary font-runcards">Upload Image File</Label>
              <Input
                id="run_card_image_file"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif"
                onChange={handleRunCardImageFileSelect}
                className="bg-white/10 border-runcards-border/20 text-runcards-text-primary file:bg-white/20 file:text-runcards-text-primary file:border-0 file:rounded-md file:px-3 file:py-1 file:mr-3 focus:outline-none focus:ring-0 focus:border-runcards-border/20 hover:border-runcards-border/20"
                style={{ 
                  outline: 'none',
                  boxShadow: 'none',
                  borderColor: 'rgba(255, 255, 255, 0.2)'
                }}
                multiple={false}
              />
              {selectedRunCardImageFile && (
                <div className="mt-2 flex items-center space-x-2">
                  <img 
                    src={runCardImagePreviewUrl} 
                    alt="Preview" 
                    className="w-16 h-16 rounded-lg object-cover border border-runcards-border/20"
                  />
                  <div className="flex-1">
                    <p className="text-runcards-text-primary text-runcards-sm">{selectedRunCardImageFile.name}</p>
                    <p className="text-runcards-text-secondary text-runcards-xs">{(selectedRunCardImageFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveRunCardImageFile}
                    className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-400/50"
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-runcards-border/20"></div>
              </div>
              <div className="relative flex justify-center text-runcards-sm">
                <span className="px-2 bg-runcards-background/90 text-runcards-text-secondary">OR</span>
              </div>
            </div>

            {/* URL Input Section */}
            <div>
              <Label htmlFor="run_card_image" className="text-runcards-text-primary font-runcards">Image URL</Label>
              <Input
                id="run_card_image"
                type="url"
                value={runCardImageData.image}
                onChange={(e) => {
                  setRunCardImageData(prev => ({ ...prev, image: e.target.value }));
                  if (selectedRunCardImageFile) {
                    handleRemoveRunCardImageFile();
                  }
                  // Clear errors when user starts typing in URL field
                  if (e.target.value.trim()) {
                    setRunCardImageErrors({});
                  }
                }}
                className={`bg-white/10 border-runcards-border/20 text-runcards-text-primary ${
                  runCardImageErrors.image ? 'border-red-400' : 'focus:border-runcards-border/60'
                }`}
                placeholder="https://example.com/your-image.jpg"
              />
              {runCardImageErrors.image && (
                <p className="text-red-400 text-runcards-sm mt-1">{runCardImageErrors.image}</p>
              )}
            </div>

            
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditingRunCardImage(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-runcards-text-primary border border-runcards-border/20"
                disabled={runCardImageLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-white text-black hover:bg-gray-100"
                disabled={runCardImageLoading}
              >
                {runCardImageLoading ? 'Updating...' : 'Update Image'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Connection Run Card Modal */}
      <Dialog open={showRunCard} onOpenChange={setShowRunCard}>
        <DialogContent className="bg-transparent border-none shadow-none max-w-fit p-0">
          {/* Hidden accessibility elements */}
          <DialogHeader className="sr-only">
            <DialogTitle>Run Card</DialogTitle>
            <DialogDescription>
              {selectedConnection?.name ? `Run card for ${selectedConnection.name}` : 'Loading run card'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="relative">
            <div className="flex justify-center">
              {loadingRunCard ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-runcards-text-primary/60 font-runcards">
                    Loading run card...
                  </div>
                </div>
              ) : selectedConnection ? (
                <RunCard 
                  runner={selectedConnection}
                  showHover={false}
                  buttonText="View Profile"
                  onButtonClick={() => {
                    toast.info('Profile details coming soon');
                  }}
                />
              ) : (
                <div className="text-center py-8">
                  <div className="text-runcards-text-primary/60 font-runcards">
                    Unable to load run card
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Club Card Modal */}
      <ScrollableClubCardModal
        clubId={user?.club_id}
        isOpen={showClubCardModal}
        onClose={() => setShowClubCardModal(false)}
      />

      {/* Feedback Modal */}
      <Dialog open={isFeedbackModalOpen} onOpenChange={setIsFeedbackModalOpen}>
        <DialogContent className="bg-runcards-background backdrop-blur-xl border border-runcards-border/20 max-w-md rounded-2xl p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-runcards-text-primary text-runcards-xl font-runcards-bold">
              Submit Feedback
            </DialogTitle>
            <DialogDescription className="text-runcards-text-secondary text-runcards-sm font-runcards mt-2">
              Share your feedback or report a bug
            </DialogDescription>
          </DialogHeader>
          <FeedbackForm 
            user={user}
            onClose={() => setIsFeedbackModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Runner Edit Profile Modal */}
      <Dialog open={isRunnerEditModalOpen} onOpenChange={setIsRunnerEditModalOpen}>
        <DialogContent className="bg-runcards-background backdrop-blur-xl border border-runcards-border/20 max-w-md rounded-2xl p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-runcards-text-primary text-runcards-xl font-runcards-bold">
              Edit Profile
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <button
              onClick={() => {
                setIsRunnerEditModalOpen(false);
                setIsPasswordModalOpen(true);
              }}
              className="w-full flex items-center px-4 py-3 bg-runcards-foreground/50 backdrop-blur-xl border border-runcards-border/20 rounded-xl text-runcards-text-primary hover:bg-white/5 transition-all duration-200"
            >
              <Key className="w-5 h-5 mr-3" />
              <span className="font-runcards flex-1 text-left">Change Password</span>
              <ChevronRight className="w-5 h-5" />
            </button>

            <button
              onClick={() => {
                setIsRunnerEditModalOpen(false);
                setIsPhoneModalOpen(true);
              }}
              className="w-full flex items-center px-4 py-3 bg-runcards-foreground/50 backdrop-blur-xl border border-runcards-border/20 rounded-xl text-runcards-text-primary hover:bg-white/5 transition-all duration-200"
            >
              <Phone className="w-5 h-5 mr-3" />
              <span className="font-runcards flex-1 text-left">Update Phone</span>
              <ChevronRight className="w-5 h-5" />
            </button>

            <button
              onClick={() => {
                setIsRunnerEditModalOpen(false);
                setIsEditingEmail(true);
              }}
              className="w-full flex items-center px-4 py-3 bg-runcards-foreground/50 backdrop-blur-xl border border-runcards-border/20 rounded-xl text-runcards-text-primary hover:bg-white/5 transition-all duration-200"
            >
              <Mail className="w-5 h-5 mr-3" />
              <span className="font-runcards flex-1 text-left">Update Email</span>
              <ChevronRight className="w-5 h-5" />
            </button>

            <button
              onClick={() => {
                setIsRunnerEditModalOpen(false);
                setIsEditingLocation(true);
                setSelectedLocation(user?.city || '');
              }}
              className="w-full flex items-center px-4 py-3 bg-runcards-foreground/50 backdrop-blur-xl border border-runcards-border/20 rounded-xl text-runcards-text-primary hover:bg-white/5 transition-all duration-200"
            >
              <MapPin className="w-5 h-5 mr-3" />
              <span className="font-runcards flex-1 text-left">Update City</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* City/Location Update Modal */}
      <Dialog open={isEditingLocation} onOpenChange={(open) => {
        setIsEditingLocation(open);
        if (!open) {
          setSelectedLocation(user?.city || '');
        }
      }}>
        <DialogContent className="bg-runcards-background backdrop-blur-xl border border-runcards-border/20 max-w-md rounded-2xl p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-runcards-text-primary text-runcards-xl font-runcards-bold">
              Update City
            </DialogTitle>
            <DialogDescription className="text-runcards-text-secondary text-runcards-sm font-runcards mt-2">
              Update your city location
            </DialogDescription>
          </DialogHeader>
          <CitySelector
            selectedCity={selectedLocation}
            onCitySelect={handleLocationSelect}
            onSave={handleLocationSelectAndSave}
            onCancel={handleLocationCancel}
            onRemove={handleLocationRemove}
            isEditing={true}
            loading={locationLoading}
            className="text-runcards-sm"
          />
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default ProfilePage;